import { Text } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { useCallback, useRef, useState } from 'react';

import { BulkModalActionType } from 'app/components/appModals/components/conversationsBulkModal';
import { CopilotAction } from 'components/copilotDropdown';
import { CopilotPinnedActions } from 'components/copilotPinnedActions';
import { ImproveReply } from 'components/copilotPinnedActions/components/improveReply';
import { SpellingAndGrammar } from 'components/copilotPinnedActions/components/spellingAndGrammar';
import {
  DEFAULT_PINNED_STATE,
  EditorCopilotDropdown,
} from 'components/editorCopilotDropdown';
import {
  EditorCopilotDropdownFeedback,
  EditorCopilotDropdownImproveReply,
  EditorCopilotDropdownSpellingAndGrammar,
  EditorCopilotDropdownTranslate,
} from 'components/editorCopilotDropdown/components';
import { EditorCopilotDropdownExpandText } from 'components/editorCopilotDropdown/components/expandText/copilotDropdownExpandText';
import { EditorMentions } from 'components/editorMentions';
import { EditorQuickRepliesDropdown } from 'components/editorQuickRepliesDropdown';
// eslint-disable-next-line no-restricted-imports
import RichTextField, {
  RichTextFieldRef,
} from 'components/shared/util/richTextField/components/richTextField';
import { copilotUtils } from 'utils/copilot';

import styles from './conversationsBulkTextEditor.module.scss';

type ConversationsBulkTextEditorProps = {
  type: Extract<BulkModalActionType, 'send_reply' | 'add_internal_note'>;
  placeholder?: string;
  /**
   * The maximum number of characters allowed. When set, a character counter is
   * shown and the count turns red once the limit is exceeded.
   */
  maxTextLength?: number | null;
  onChange: (html: string, plain: string) => void;
};

export const ConversationsBulkTextEditor = ({
  placeholder,
  type,
  maxTextLength,
  onChange,
}: ConversationsBulkTextEditorProps): JSX.Element => {
  const editorRef = useRef<RichTextFieldRef | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [plainTextLength, setPlainTextLength] = useState(0);

  const handleChange = useCallback(() => {
    const plainText = editorRef.current?.getPlainText() || '';
    setPlainTextLength(plainText.length);
    onChange(editorRef.current?.getText() || '', plainText);
  }, [onChange]);

  const hasMaxTextLength =
    typeof maxTextLength === 'number' && maxTextLength > 0;
  const isOverMaxTextLength =
    hasMaxTextLength && plainTextLength > maxTextLength;

  const editorCopilotActions = {
    improveReply: {
      title: 'Improve note' as string,
      icon: 'pen',
      commandName: 'richTextEditor:improveReply',
      content: (
        <EditorCopilotDropdownImproveReply
          lastMessage=""
          disabledImprovements={[
            {
              value: 'match_tone',
              tooltip: 'Not available for internal notes',
            },
          ]}
        />
      ),
      isPinned: ({ pinnedState }) =>
        pinnedState?.improveReply ?? DEFAULT_PINNED_STATE.improveReply,
    },
    translate: {
      title: 'Translate',
      icon: 'translate',
      commandName: 'richTextEditor:translate',
      content: <EditorCopilotDropdownTranslate />,
    },
    checkSpellingAndGrammar: {
      title: 'Fix spelling and grammar',
      commandName: 'richTextEditor:checkSpellingAndGrammar',
      icon: 'grammar-spelling',
      content: <EditorCopilotDropdownSpellingAndGrammar />,
      isPinned: ({ pinnedState }) =>
        pinnedState?.checkSpellingAndGrammar ??
        DEFAULT_PINNED_STATE.checkSpellingAndGrammar,
      isDisabled: ({ currentEditorText }) => {
        const { isDisabled, tooltipContent } =
          copilotUtils.spellingAndGrammar.getStatus({
            text: currentEditorText || '',
          });

        if (isDisabled) {
          return tooltipContent;
        }
        return false;
      },
    },
    expandText: {
      title: 'Expand text',
      commandName: 'richTextEditor:expandText',
      icon: 'chat-plus',
      content: <EditorCopilotDropdownExpandText lastMessage="" />,
      isPinned: ({ pinnedState }) =>
        pinnedState?.expandText ?? DEFAULT_PINNED_STATE.expandText,
      isDisabled: ({ currentEditorText }) => {
        const { isDisabled, tooltipContent } =
          copilotUtils.expandText.getStatus({
            text: currentEditorText || '',
          });

        if (isDisabled) {
          return tooltipContent;
        }

        return false;
      },
    },
    sendFeedback: {
      title: 'Send feedback',
      icon: 'paper-plane',
      hasDividerBefore: true,
      content: <EditorCopilotDropdownFeedback />,
    },
  } satisfies Record<string, CopilotAction>;

  return (
    <>
      <div
        className={classnames(styles.container, {
          [styles.containerInternalNote]: type === 'add_internal_note',
        })}
        ref={containerRef}
      >
        <RichTextField
          ref={editorRef}
          channel="bulk"
          placeholder={
            placeholder ??
            (type === 'add_internal_note'
              ? 'Type your internal note...'
              : 'Type your message...')
          }
          onChange={handleChange}
          toolbarAddons={
            <>
              {type === 'add_internal_note' && (
                <EditorMentions container={containerRef} />
              )}
              <EditorQuickRepliesDropdown
                popoverContent={{ side: 'bottom', align: 'start' }}
              />
              <EditorCopilotDropdown
                editorCopilotActions={editorCopilotActions}
              />
              <CopilotPinnedActions
                text={editorRef.current?.getText() || ''}
                pinnableActions={[
                  {
                    id: 'improveReply',
                    component: ImproveReply,
                    condition: (args) => args.pinState['improveReply'],
                    label: 'Improving note',
                    disabledImprovements: ['match_tone'],
                  },
                  {
                    id: 'checkSpellingAndGrammar',
                    component: SpellingAndGrammar,
                    condition: (args) =>
                      args.pinState['checkSpellingAndGrammar'],
                  },
                ]}
              />
            </>
          }
          className={styles.editor}
        />
      </div>
      {hasMaxTextLength && (
        <div className={styles.charCountRow}>
          {isOverMaxTextLength && (
            <Text.Paragraph variant="4" className={styles.charCountError}>
              Your reply exceeds the {maxTextLength} character limit supported
              by some selected channels.
            </Text.Paragraph>
          )}
          <Text.Paragraph
            variant="4"
            className={classnames(styles.charCount, {
              [styles.charCountError]: isOverMaxTextLength,
            })}
          >
            {plainTextLength} / {maxTextLength}
          </Text.Paragraph>
        </div>
      )}
    </>
  );
};
